import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { db, auth, functions, storage } from '../services/firebase'
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { httpsCallable } from 'firebase/functions'
import './AdminConfigPage.css'

function AdminConfigPage() {
    const location = useLocation()
    const [activeTab, setActiveTab] = useState('marks')

    const [catalogs, setCatalogs] = useState([])
    const [marketplaces, setMarketplaces] = useState([])
    const [loading, setLoading] = useState(true)
    const [scraping, setScraping] = useState(false)
    const [scrapeProgress, setScrapeProgress] = useState(null) // { current, total, startTime, estimatedFinish }
    const [scrapeResult, setScrapeResult] = useState(null)

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const tab = params.get('tab')
        if (tab && ['marks', 'catalogs', 'marketplaces', 'maintenance'].includes(tab)) {
            setActiveTab(tab)
        }
    }, [location])

    // Mark form state
    const [markForm, setMarkForm] = useState({
        manufacturer: '',
        country: '',
        period: '',
        description: '',
        catalogUrl: '',
        tags: '',
        imageFile: null
    })

    useEffect(() => {
        loadData()
    }, [activeTab])

    const loadData = async () => {
        setLoading(true)
        try {
            if (activeTab === 'marks') {
                // No longer fetching marks list here for performance (list is on separate page)
            } else if (activeTab === 'catalogs') {
                const snapshot = await getDocs(collection(db, 'catalogSources'))
                setCatalogs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
            } else if (activeTab === 'marketplaces') {
                const snapshot = await getDocs(collection(db, 'marketplaceSources'))
                setMarketplaces(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
            }
        } catch (err) {
            console.error('Error loading data:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleMarkSubmit = async (e) => {
        e.preventDefault()
        if (!markForm.imageFile) {
            alert('Please select a mark image')
            return
        }

        try {
            // Upload image to Storage
            const storageRef = ref(storage, `marks/${Date.now()}_${markForm.imageFile.name}`)
            await uploadBytes(storageRef, markForm.imageFile)
            const imageUrl = await getDownloadURL(storageRef)

            // Save to Firestore
            await addDoc(collection(db, 'marks'), {
                manufacturer: markForm.manufacturer,
                country: markForm.country,
                period: markForm.period,
                description: markForm.description,
                catalogUrl: markForm.catalogUrl,
                tags: markForm.tags.split(',').map(t => t.trim()),
                markImageUrl: imageUrl,
                createdAt: serverTimestamp(),
                createdBy: auth.currentUser.uid
            })

            alert('Mark added successfully!')
            setMarkForm({ manufacturer: '', country: '', period: '', description: '', catalogUrl: '', tags: '', imageFile: null })
            loadData()
        } catch (err) {
            console.error('Error adding mark:', err)
            alert('Failed to add mark')
        }
    }

    const handleDeleteMark = async (id) => {
        if (!confirm('Delete this mark?')) return
        try {
            await deleteDoc(doc(db, 'marks', id))
            loadData()
        } catch (err) {
            console.error('Error deleting mark:', err)
        }
    }

    const handleScrapeCatalogs = async (sourceId = null) => {
        const source = catalogs.find(c => c.id === sourceId);
        const confirmMsg = sourceId
            ? `Start automated scraping for ${source?.name}? This will process all manufacturers in batches.`
            : 'Start scraping all enabled catalogs? This may take several minutes.';

        if (!confirm(confirmMsg)) return

        setScraping(true)
        setScrapeResult(null)
        setScrapeProgress({
            current: source?.lastProcessedOffset || 0,
            total: source?.totalManufacturers || 1000,
            startTime: Date.now()
        })

        try {
            const scrapeFn = httpsCallable(functions, 'scrapeCatalogsV1', { timeout: 600000 });
            let isDone = false;
            let totalAdded = 0;
            let currentOffset = source?.lastProcessedOffset || 0;

            while (!isDone) {
                const response = await scrapeFn({ sourceId });
                const result = response.data;

                if (!result.success) throw new Error(result.error || 'Scraping failed');

                totalAdded += result.summary.totalMarks;
                currentOffset = result.summary.nextOffset !== undefined ? result.summary.nextOffset : currentOffset;

                // Refresh data to get latest offset from DB
                const snapshot = await getDocs(collection(db, 'catalogSources'));
                const updatedSource = snapshot.docs.find(d => d.id === sourceId)?.data();

                const progress = {
                    current: updatedSource?.lastProcessedOffset || 0,
                    total: updatedSource?.totalManufacturers || 100,
                    batchAdded: result.summary.totalMarks,
                    startTime: scrapeProgress?.startTime || Date.now()
                };

                // Estimate finish time
                const elapsed = (Date.now() - progress.startTime) / 1000;
                const processed = progress.current - (source?.lastProcessedOffset || 0);
                if (processed > 0) {
                    const rate = processed / elapsed; // items per second
                    const remaining = progress.total - progress.current;
                    const timeToFinish = remaining / rate;
                    progress.estimatedFinish = new Date(Date.now() + timeToFinish * 1000).toLocaleTimeString();
                }

                setScrapeProgress(progress);
                setScrapeResult(result);

                // Stop if we hit the end (offset resets to 0 or doesn't move)
                if (result.summary.nextOffset === 0) {
                    isDone = true;
                }

                // If sourceId is null (scrape all), we don't loop here for now to keep it simple
                if (!sourceId) isDone = true;

                if (!isDone) {
                    await new Promise(r => setTimeout(r, 1000)); // Respectful delay between batches
                }
            }

            alert(`Automated scraping complete! Total marks added: ${totalAdded}`);
            loadData()
        } catch (err) {
            console.error('Scraping error:', err)
            alert(`Scraping interrupted: ${err.message}`)
        } finally {
            setScraping(false)
            setScrapeProgress(null)
        }
    }

    const handleClearCatalogData = async (sourceId) => {
        const source = catalogs.find(c => c.id === sourceId);
        if (!confirm(`⚠️ CRITICAL ACTION: Really delete ALL hallmarks from "${source?.name}"? This cannot be undone.`)) return;

        setScraping(true);
        try {
            const clearFn = httpsCallable(functions, 'clearCatalogMarksV1');
            const response = await clearFn({ sourceId });
            const result = response.data;

            if (result.success) {
                alert(`Successfully cleared ${result.deletedCount} marks from ${source?.name}.`);
                loadData();
            } else {
                alert(`Failed to clear data: ${result.error || 'Unknown error'}`);
            }
        } catch (err) {
            console.error('Clear error:', err);
            alert(`Failed to clear data: ${err.message}`);
        } finally {
            setScraping(false);
        }
    };

    const handleSyncCounts = async () => {
        if (!confirm('Recalculate all mark counts? This will fix metadata inflation from previous scrapes.')) return;
        setScraping(true);
        try {
            const res = await fetch('https://europe-west1-porcelain-identification-78c76.cloudfunctions.net/syncSourceCounts?repair-secret=porcelain-repair-2024');
            const result = await res.json();
            if (result.success) {
                alert(`Sync Complete!\n${result.results.map(r => `${r.source}: ${r.old} → ${r.new}`).join('\n')}`);
                loadData();
            } else {
                alert(`Sync Failed: ${result.error}`);
            }
        } catch (err) {
            alert(`Error: ${err.message}`);
        } finally {
            setScraping(false);
        }
    };

    const getProxyUrl = (originalUrl) => {
        if (!originalUrl) return '';
        if (originalUrl.includes('firebasestorage.googleapis.com')) return originalUrl;
        if (originalUrl.includes('antikvarchik.ru')) {
            // Function URL should ideally be dynamic but for now we'll use the suspected production URL or relative
            // Relative works if hosting and functions are same origin/linked
            // But Cloud Functions are usually on a different domain.
            const region = 'europe-west1';
            const project = 'porcelain-identification-78c76';
            return `https://${region}-${project}.cloudfunctions.net/proxyImage?url=${encodeURIComponent(originalUrl)}`;
        }
        return originalUrl;
    };

    // Catalog Sources Manager Component
    const CatalogSourcesManager = ({ catalogs, onReload, onSync, onScrape, scraping, scrapeResult }) => {
        const [newCatalog, setNewCatalog] = useState({ name: '', url: '', type: 'hallmarks', description: '' })
        const [editingId, setEditingId] = useState(null)
        const [editForm, setEditForm] = useState({})

        const handleAddCatalog = async (e) => {
            e.preventDefault()
            try {
                await addDoc(collection(db, 'catalogSources'), {
                    ...newCatalog,
                    enabled: true,
                    createdAt: serverTimestamp(),
                    createdBy: auth.currentUser.uid
                })
                setNewCatalog({ name: '', url: '', type: 'hallmarks', description: '' })
                onReload()
            } catch (err) {
                console.error('Error adding catalog:', err)
                alert('Failed to add catalog source')
            }
        }

        const handleUpdateCatalog = async (id) => {
            try {
                await updateDoc(doc(db, 'catalogSources', id), {
                    ...editForm,
                    updatedAt: serverTimestamp()
                })
                setEditingId(null)
                onReload()
            } catch (err) {
                console.error('Error updating catalog:', err)
            }
        }

        const handleDeleteCatalog = async (id) => {
            if (!confirm('Delete this catalog source?')) return
            try {
                await deleteDoc(doc(db, 'catalogSources', id))
                onReload()
            } catch (err) {
                console.error('Error deleting catalog:', err)
            }
        }

        const handleToggleEnabled = async (id, currentState) => {
            try {
                await updateDoc(doc(db, 'catalogSources', id), { enabled: !currentState })
                onReload()
            } catch (err) {
                console.error('Error toggling catalog:', err)
            }
        }

        return (
            <>
                <fieldset className="add-form">
                    <legend>Add Catalog Source</legend>
                    <form onSubmit={handleAddCatalog}>
                        <div className="form-row">
                            <input
                                type="text"
                                placeholder="Catalog Name *"
                                value={newCatalog.name}
                                onChange={(e) => setNewCatalog({ ...newCatalog, name: e.target.value })}
                                required
                            />
                            <select
                                value={newCatalog.type}
                                onChange={(e) => setNewCatalog({ ...newCatalog, type: e.target.value })}
                            >
                                <option value="hallmarks">Hallmarks</option>
                                <option value="pottery">Pottery Marks</option>
                                <option value="porcelain">Porcelain</option>
                                <option value="glass">Glass</option>
                                <option value="general">General</option>
                            </select>
                        </div>
                        <input
                            type="url"
                            placeholder="Catalog URL *"
                            value={newCatalog.url}
                            onChange={(e) => setNewCatalog({ ...newCatalog, url: e.target.value })}
                            required
                            className="full-width"
                        />
                        <textarea
                            placeholder="Description (optional)"
                            value={newCatalog.description}
                            onChange={(e) => setNewCatalog({ ...newCatalog, description: e.target.value })}
                        />
                        <button type="submit" className="btn btn-primary">Add Catalog Source</button>
                    </form>
                </fieldset>

                <div className="sources-list">
                    <div className="sources-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2>Configured Catalog Sources ({catalogs.length})</h2>
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => onSync()}
                            disabled={scraping}
                            style={{ padding: '4px 12px', fontSize: '0.8rem' }}
                        >
                            🔄 Sync Counts
                        </button>
                    </div>

                    {scraping && scrapeProgress && (
                        <div className="scrape-progress card-glass">
                            <div className="progress-header">
                                <h3>Scraping in progress...</h3>
                                {scrapeProgress.estimatedFinish && (
                                    <span className="estimate">Est. Finish: {scrapeProgress.estimatedFinish}</span>
                                )}
                            </div>
                            <div className="progress-bar-container">
                                <div
                                    className="progress-bar"
                                    style={{ width: `${Math.min(100, (scrapeProgress.current / scrapeProgress.total) * 100)}%` }}
                                ></div>
                                {scrapeProgress.current > 0 && (
                                    <p className="progress-detail">
                                        Processed {scrapeProgress.current} / {scrapeProgress.total} categories.
                                        {scrapeProgress.batchAdded !== undefined && ` Last batch: +${scrapeProgress.batchAdded} marks.`}
                                    </p>
                                )}
                            </div>
                            <div className="progress-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Categories: {scrapeProgress.current} / {scrapeProgress.total}</span>
                                <span style={{ marginLeft: '10px' }}>{Math.round((scrapeProgress.current / scrapeProgress.total) * 100)}%</span>
                            </div>
                        </div>
                    )}

                    {scrapeResult && !scraping && (
                        <div className="scrape-result">
                            <p>✅ Scraped {scrapeResult.summary?.totalMarks || 0} marks</p>
                            {scrapeResult.summary?.errors > 0 && (
                                <p className="error">⚠️ {scrapeResult.summary.errors} errors occurred</p>
                            )}
                        </div>
                    )}
                    {catalogs.length === 0 ? (
                        <p className="empty-message">No catalog sources configured. Add one above to start scraping hallmarks.</p>
                    ) : (
                        <div className="sources-grid">
                            {catalogs.map(catalog => (
                                <div key={catalog.id} className={`source-card ${catalog.enabled ? 'enabled' : 'disabled'}`}>
                                    {editingId === catalog.id ? (
                                        <div className="edit-form">
                                            <input
                                                type="text"
                                                value={editForm.name}
                                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                            />
                                            <input
                                                type="url"
                                                value={editForm.url}
                                                onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
                                            />
                                            <div className="edit-actions">
                                                <button onClick={() => handleUpdateCatalog(catalog.id)} className="btn-save">Save</button>
                                                <button onClick={() => setEditingId(null)} className="btn-cancel">Cancel</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="source-header">
                                                <h3>{catalog.name}</h3>
                                                <span className={`status-badge ${catalog.enabled ? 'active' : 'inactive'}`}>
                                                    {catalog.enabled ? 'Active' : 'Disabled'}
                                                </span>
                                            </div>
                                            <p className="source-url">
                                                {(() => {
                                                    try {
                                                        return decodeURIComponent(catalog.url);
                                                    } catch (e) {
                                                        return catalog.url;
                                                    }
                                                })()}
                                            </p>
                                            <p className="source-type">Type: {catalog.type}</p>
                                            {catalog.description && <p className="source-desc">{catalog.description}</p>}
                                            <div className="source-meta">
                                                {catalog.lastScrapedAt && (
                                                    <span className="meta-item">
                                                        📅 Last Scraped: {new Date(catalog.lastScrapedAt?.toDate?.() || catalog.lastScrapedAt).toLocaleString()}
                                                    </span>
                                                )}
                                                {catalog.marksCount !== undefined && (
                                                    <span className="meta-item">
                                                        🏷️ Marks: {catalog.marksCount}
                                                    </span>
                                                )}
                                                {catalog.totalManufacturers !== undefined && (
                                                    <span className="meta-item" title="Progress through manufacturer list">
                                                        📊 Progress: {catalog.lastProcessedOffset || 0} / {catalog.totalManufacturers} mfg
                                                    </span>
                                                )}
                                            </div>
                                            <div className="source-actions">
                                                <button onClick={() => { setEditingId(catalog.id); setEditForm(catalog) }} className="btn-edit">Edit</button>
                                                <button
                                                    onClick={() => onScrape(catalog.id)}
                                                    disabled={scraping || !catalog.enabled}
                                                    className="btn-scrape-small"
                                                    title="Scrape this source specifically"
                                                >
                                                    {scraping ? '...' : 'Scrape'}
                                                </button>
                                                <button onClick={() => handleToggleEnabled(catalog.id, catalog.enabled)} className="btn-toggle">
                                                    {catalog.enabled ? 'Disable' : 'Enable'}
                                                </button>
                                                <button onClick={() => handleDeleteCatalog(catalog.id)} className="btn-delete">Delete Source</button>
                                                <button
                                                    onClick={() => handleClearCatalogData(catalog.id)}
                                                    disabled={scraping}
                                                    className="btn-clear-data"
                                                    title="Delete all marks scraped from this source"
                                                >
                                                    {scraping ? '...' : 'Clear Marks'}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </>
        )
    }

    // Marketplace Manager Component
    const MarketplaceManager = ({ marketplaces, onReload }) => {
        const [newMarket, setNewMarket] = useState({ name: '', url: '', apiKey: '', type: 'search' })
        const [editingId, setEditingId] = useState(null)
        const [editForm, setEditForm] = useState({})
        const location = useLocation()

        useEffect(() => {
            const params = new URLSearchParams(location.search)
            const editId = params.get('edit')
            if (editId) {
                const marketToEdit = marketplaces.find(m => m.id === editId)
                if (marketToEdit) {
                    setEditingId(editId)
                    setEditForm(marketToEdit)
                }
            }
        }, [location, marketplaces])

        const handleAddMarketplace = async (e) => {
            e.preventDefault()
            try {
                await addDoc(collection(db, 'marketplaceSources'), {
                    ...newMarket,
                    enabled: true,
                    createdAt: serverTimestamp()
                })
                setNewMarket({ name: '', url: '', apiKey: '', type: 'search' })
                onReload()
            } catch (err) {
                console.error('Error adding marketplace:', err)
            }
        }

        const handleUpdateMarketplace = async (id) => {
            try {
                await updateDoc(doc(db, 'marketplaceSources', id), {
                    ...editForm,
                    updatedAt: serverTimestamp()
                })
                setEditingId(null)
                onReload()
            } catch (err) {
                console.error('Error updating marketplace:', err)
            }
        }

        const handleDeleteMarketplace = async (id) => {
            if (!confirm('Delete this marketplace?')) return
            try {
                await deleteDoc(doc(db, 'marketplaceSources', id))
                onReload()
            } catch (err) {
                console.error('Error deleting marketplace:', err)
            }
        }

        const handleToggleEnabled = async (id, currentState) => {
            try {
                await updateDoc(doc(db, 'marketplaceSources', id), { enabled: !currentState })
                onReload()
            } catch (err) {
                console.error('Error toggling marketplace:', err)
            }
        }

        return (
            <>
                <fieldset className="add-form">
                    <legend>Add Marketplace</legend>
                    <form onSubmit={handleAddMarketplace}>
                        <div className="form-row">
                            <input
                                type="text"
                                placeholder="Marketplace Name *"
                                value={newMarket.name}
                                onChange={(e) => setNewMarket({ ...newMarket, name: e.target.value })}
                                required
                            />
                            <select
                                value={newMarket.type}
                                onChange={(e) => setNewMarket({ ...newMarket, type: e.target.value })}
                            >
                                <option value="search">Web Search</option>
                                <option value="api">API Integration</option>
                                <option value="scrape">Web Scraping</option>
                            </select>
                        </div>
                        <input
                            type="url"
                            placeholder="Marketplace URL or API Endpoint *"
                            value={newMarket.url}
                            onChange={(e) => setNewMarket({ ...newMarket, url: e.target.value })}
                            required
                            className="full-width"
                        />
                        <input
                            type="text"
                            placeholder="API Key (if required)"
                            value={newMarket.apiKey}
                            onChange={(e) => setNewMarket({ ...newMarket, apiKey: e.target.value })}
                            className="full-width"
                        />
                        {newMarket.type === 'api' && (
                            <div className="form-row">
                                <input
                                    type="text"
                                    placeholder="Client ID (App ID)"
                                    value={newMarket.clientId || ''}
                                    onChange={(e) => setNewMarket({ ...newMarket, clientId: e.target.value })}
                                />
                                <input
                                    type="password"
                                    placeholder="Client Secret (Cert ID)"
                                    value={newMarket.clientSecret || ''}
                                    onChange={(e) => setNewMarket({ ...newMarket, clientSecret: e.target.value })}
                                />
                            </div>
                        )}
                        <button type="submit" className="btn btn-primary">Add Marketplace</button>
                    </form>
                </fieldset>

                <div className="sources-list">
                    <h2>Configured Marketplaces ({marketplaces.length})</h2>
                    <div className="sources-grid">
                        {marketplaces.map(market => (
                            <div key={market.id} className={`source-card ${market.enabled === false ? 'disabled' : ''}`}>
                                {editingId === market.id ? (
                                    <div className="edit-form">
                                        <input
                                            type="text"
                                            value={editForm.name}
                                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                        />
                                        <input
                                            type="url"
                                            value={editForm.url}
                                            onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
                                        />
                                        <select
                                            value={editForm.type}
                                            onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                                        >
                                            <option value="search">Web Search</option>
                                            <option value="api">API Integration</option>
                                            <option value="scrape">Web Scraping</option>
                                        </select>
                                        <input
                                            type="text"
                                            placeholder="API Key"
                                            value={editForm.apiKey}
                                            onChange={(e) => setEditForm({ ...editForm, apiKey: e.target.value })}
                                        />
                                        {editForm.type === 'api' && (
                                            <>
                                                <input
                                                    type="text"
                                                    placeholder="Client ID (App ID)"
                                                    value={editForm.clientId || ''}
                                                    onChange={(e) => setEditForm({ ...editForm, clientId: e.target.value })}
                                                />
                                                <input
                                                    type="password"
                                                    placeholder="Client Secret (Cert ID)"
                                                    value={editForm.clientSecret || ''}
                                                    onChange={(e) => setEditForm({ ...editForm, clientSecret: e.target.value })}
                                                />
                                            </>
                                        )}
                                        <div className="edit-actions">
                                            <button onClick={() => handleUpdateMarketplace(market.id)} className="btn-save">Save</button>
                                            <button onClick={() => setEditingId(null)} className="btn-cancel">Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="source-header">
                                            <h3>{market.name}</h3>
                                            <span className={`status-badge ${market.enabled !== false ? 'active' : 'inactive'}`}>
                                                {market.enabled !== false ? 'Active' : 'Disabled'}
                                            </span>
                                        </div>
                                        <p className="source-url">{market.url}</p>
                                        <p className="source-type">Type: {market.type}</p>
                                        <div className="source-actions">
                                            <button onClick={() => { setEditingId(market.id); setEditForm(market) }} className="btn-edit">Edit</button>
                                            <button onClick={() => handleToggleEnabled(market.id, market.enabled !== false)} className="btn-toggle">
                                                {market.enabled !== false ? 'Disable' : 'Enable'}
                                            </button>
                                            <button onClick={() => handleDeleteMarketplace(market.id)} className="btn-delete">Delete</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </>
        )
    }

    return (
        <div className="admin-config-page">
            <div className="container">
                <div className="config-header">
                    <Link to="/admin" className="back-link">← Back to Dashboard</Link>
                    <h1>Admin Configuration</h1>
                    <p>Manage hallmark sources, catalogs, and marketplace integrations</p>
                </div>

                <div className="config-layout">
                    {/* Sidebar Navigation (Option A) */}
                    <aside className="config-sidebar">
                        <nav className="sidebar-nav">
                            <button
                                className={activeTab === 'marks' ? 'active' : ''}
                                onClick={() => setActiveTab('marks')}
                            >
                                📦 Mark Database
                            </button>
                            <button
                                className={activeTab === 'catalogs' ? 'active' : ''}
                                onClick={() => setActiveTab('catalogs')}
                            >
                                📚 Catalog Sources
                            </button>
                            <button
                                className={activeTab === 'marketplaces' ? 'active' : ''}
                                onClick={() => setActiveTab('marketplaces')}
                            >
                                🛒 Marketplaces
                            </button>
                            <button
                                className={activeTab === 'maintenance' ? 'active' : ''}
                                onClick={() => setActiveTab('maintenance')}
                            >
                                🔧 Maintenance
                            </button>
                        </nav>
                    </aside>

                    {/* Main Content Area */}
                    <div className="config-content">

                        {/* Mark Management */}
                        {activeTab === 'marks' && (
                            <div className="tab-content">
                                <div className="database-launch-card card-glass mb-4">
                                    <div className="launch-info">
                                        <h2>Hallmark Database</h2>
                                        <p>Manage thousands of marks with search and pagination.</p>
                                    </div>
                                    <Link to="/admin/marks" className="btn btn-primary">
                                        Open Database ↗
                                    </Link>
                                </div>

                                <fieldset>
                                    <legend>Add New Mark</legend>
                                    <form onSubmit={handleMarkSubmit}>
                                        <div className="form-row">
                                            <div>
                                                <label>Manufacturer</label>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. Meissen"
                                                    value={markForm.manufacturer}
                                                    onChange={(e) => setMarkForm({ ...markForm, manufacturer: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label>Country</label>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. Germany"
                                                    value={markForm.country}
                                                    onChange={(e) => setMarkForm({ ...markForm, country: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div>
                                                <label>Period</label>
                                                <input
                                                    type="text"
                                                    placeholder="1920-1945"
                                                    value={markForm.period}
                                                    onChange={(e) => setMarkForm({ ...markForm, period: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <label>Catalog URL</label>
                                                <input
                                                    type="text"
                                                    value={markForm.catalogUrl}
                                                    onChange={(e) => setMarkForm({ ...markForm, catalogUrl: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <label>Description</label>
                                        <textarea
                                            value={markForm.description}
                                            onChange={(e) => setMarkForm({ ...markForm, description: e.target.value })}
                                            rows="3"
                                        />
                                        <label>Tags (comma-separated)</label>
                                        <input
                                            type="text"
                                            value={markForm.tags}
                                            onChange={(e) => setMarkForm({ ...markForm, tags: e.target.value })}
                                        />
                                        <div className="file-input-wrapper" style={{ marginBottom: '1rem' }}>
                                            <label>Mark Image (Required):</label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => setMarkForm({ ...markForm, imageFile: e.target.files[0] })}
                                                required
                                                style={{ border: 'none', padding: 0 }}
                                            />
                                        </div>
                                        <button type="submit" className="btn btn-primary">Add Mark to Database</button>
                                    </form>
                                </fieldset>
                            </div>
                        )}

                        {/* Catalog Sources */}
                        {activeTab === 'catalogs' && (
                            <div className="tab-content">
                                <CatalogSourcesManager
                                    catalogs={catalogs}
                                    onReload={loadData}
                                    onSync={handleSyncCounts}
                                    onScrape={handleScrapeCatalogs}
                                    scraping={scraping}
                                    scrapeResult={scrapeResult}
                                />
                            </div>
                        )}

                        {/* Marketplaces */}
                        {activeTab === 'marketplaces' && (
                            <div className="tab-content">
                                <MarketplaceManager
                                    marketplaces={marketplaces}
                                    onReload={loadData}
                                />
                            </div>
                        )}

                        {/* Maintenance & Tools */}
                        {activeTab === 'maintenance' && (
                            <div className="tab-content">
                                <fieldset className="mb-4">
                                    <legend>Storage Management</legend>
                                    <p className="mb-4">Clean up images from failed or old identification requests to save storage space.</p>

                                    <div className="cleanup-controls">
                                        <div className="form-row">
                                            <div className="input-group">
                                                <label>Cleanup threshold (days):</label>
                                                <input
                                                    type="number"
                                                    id="cleanupDays"
                                                    defaultValue="30"
                                                    min="0"
                                                />
                                            </div>
                                            <div className="input-group">
                                                <label>Batch limit:</label>
                                                <input
                                                    type="number"
                                                    id="cleanupLimit"
                                                    defaultValue="100"
                                                    min="1"
                                                />
                                            </div>
                                        </div>
                                        <div className="action-buttons">
                                            <button
                                                className="btn btn-secondary"
                                                onClick={async () => {
                                                    const days = document.getElementById('cleanupDays').value;
                                                    const limit = document.getElementById('cleanupLimit').value;
                                                    setScraping(true);
                                                    try {
                                                        const url = `https://cleanupstaleidentificationsv1-hlhgvkwroq-ew.a.run.app?days=${days}&limit=${limit}&simulate=true`;
                                                        const response = await fetch(url);
                                                        const result = await response.json();
                                                        alert(`SIMULATION COMPLETE:\nProcessed: ${result.processed}\nPotential Deletions: ${result.deletedCount}\nMessage: ${result.message}`);
                                                    } catch (err) {
                                                        alert(`Error: ${err.message}`);
                                                    } finally {
                                                        setScraping(false);
                                                    }
                                                }}
                                                disabled={scraping}
                                            >
                                                Run Simulation
                                            </button>
                                            <button
                                                className="btn btn-danger"
                                                onClick={async () => {
                                                    const days = document.getElementById('cleanupDays').value;
                                                    const limit = document.getElementById('cleanupLimit').value;
                                                    if (!confirm(`⚠️ REALLY DELETE images from ${days} days ago? This is permanent.`)) return;

                                                    setScraping(true);
                                                    try {
                                                        const url = `https://cleanupstaleidentificationsv1-hlhgvkwroq-ew.a.run.app?days=${days}&limit=${limit}&simulate=false`;
                                                        const response = await fetch(url);
                                                        const result = await response.json();
                                                        alert(`CLEANUP COMPLETE:\nProcessed: ${result.processed}\nImages Deleted: ${result.deletedCount}\nMessage: ${result.message}`);
                                                    } catch (err) {
                                                        alert(`Error: ${err.message}`);
                                                    } finally {
                                                        setScraping(false);
                                                    }
                                                }}
                                                disabled={scraping}
                                            >
                                                {scraping ? 'Working...' : 'Batch Remove Old Records'}
                                            </button>
                                        </div>
                                    </div>
                                </fieldset>

                                <div className="card-glass">
                                    <h2>System Health</h2>
                                    <p>Current Identification System Status: <strong>Active (Parallelized)</strong></p>
                                    <p>Database Coverage: <strong>~12,500 hallmarks hashed</strong></p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div >
    )
}

export default AdminConfigPage
